import React, { useState, useEffect } from 'react'
import {
  ArrowLeft,
  Edit,
  User,
  Calendar,
  Tag,
  Building,
  MessageSquare,
  Send,
  Paperclip,
  Download
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import supportTicketService from '../../services/supportTicketService'

const priorityClass = (p) => {
  if (p === 'Critical') return 'critical'
  if (p === 'High') return 'high'
  if (p === 'Medium') return 'medium'
  return 'low'
}

const SupportTicketView = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [ticket, setTicket] = useState(null)
  const [loading, setLoading] = useState(true)
  const [commentText, setCommentText] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)

  useEffect(() => {
    if (id) loadTicket()
  }, [id])

  const loadTicket = async () => {
    try {
      setLoading(true)
      const res = await supportTicketService.getById(id)
      if (res.success && res.data) {
        setTicket(res.data)
      } else {
        toast.error('Ticket not found')
        navigate('/support/support-tickets')
      }
    } catch (e) {
      console.error('Error loading ticket:', e)
      toast.error('Failed to load ticket')
      navigate('/support/support-tickets')
    } finally {
      setLoading(false)
    }
  }

  const handleAddComment = async (e) => {
    e.preventDefault()
    if (!commentText?.trim()) return
    try {
      setSubmittingComment(true)
      const res = await supportTicketService.addComment(id, commentText.trim())
      if (res.success) {
        setTicket(res.data)
        setCommentText('')
        toast.success('Comment added')
      } else {
        toast.error(res.message || 'Failed to add comment')
      }
    } catch (e) {
      toast.error('Failed to add comment')
    } finally {
      setSubmittingComment(false)
    }
  }

  const handleEdit = () => navigate(`/support/support-tickets/edit/${id}`)

  if (loading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading ticket...</p>
        </div>
      </div>
    )
  }

  if (!ticket) return null

  const breadcrumbItems = [
    { label: 'Support', path: '/support' },
    { label: 'Support Tickets', path: '/support/support-tickets' },
    { label: ticket.ticketId || 'View', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard support-ticket-view">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>
      <div className="page-header">
        <div className="page-title">
          <h1>
            <span className="ticket-id">{ticket.ticketId}</span>
            {ticket.subject}
          </h1>
          <div className="ticket-meta">
            <span className={`status-badge ${(ticket.status || '').toLowerCase().replace(/\s/g, '-')}`}>
              {ticket.status}
            </span>
            <span className={`priority-badge priority-${priorityClass(ticket.priority)}`}>
              {ticket.priority}
            </span>
            <span className={`type-badge type-${(ticket.type || '').toLowerCase()}`}>
              {ticket.type}
            </span>
            {ticket.category && <span className="category-badge">{ticket.category}</span>}
          </div>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleEdit}>
            <Edit size={18} />
            Edit
          </button>
          <button className="btn-secondary" onClick={() => navigate('/support/support-tickets')}>
            <ArrowLeft size={18} />
            Back
          </button>
        </div>
      </div>
      <div className="ticket-view-grid">
        <div className="ticket-main">
          <div className="ticket-description card">
            <h3>Description</h3>
            <p>{ticket.description || 'No description provided.'}</p>
          </div>
          <div className="ticket-comments card">
            <h3><MessageSquare size={18} /> Comments ({ticket.comments?.length || 0})</h3>
            <form onSubmit={handleAddComment} className="comment-form">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder="Add a comment..."
                rows={3}
                disabled={submittingComment}
              />
              <button type="submit" className="btn-primary" disabled={submittingComment || !commentText?.trim()}>
                <Send size={16} />
                {submittingComment ? 'Adding...' : 'Add Comment'}
              </button>
            </form>
            <div className="comments-list">
              {(ticket.comments || []).map((c, idx) => (
                <div key={c._id || idx} className="comment-item">
                  <div className="comment-author">
                    {(c.author?.name || c.author?.userName || c.authorName || 'Support').charAt(0).toUpperCase()}
                  </div>
                  <div className="comment-body">
                    <div className="comment-header">
                      <strong>{c.author?.name || c.author?.userName || c.authorName || 'Support'}</strong>
                      <span className="comment-date">
                        {(c.createdAt || c.at) ? new Date(c.createdAt || c.at).toLocaleString() : ''}
                      </span>
                    </div>
                    <p>{c.body}</p>
                  </div>
                </div>
              ))}
              {(!ticket.comments || ticket.comments.length === 0) && (
                <p className="text-muted">No comments yet.</p>
              )}
            </div>
          </div>
        </div>
        <div className="ticket-sidebar">
          <div className="card">
            <h3>Details</h3>
            <div className="detail-row">
              <User size={16} />
              <span>Assignee</span>
              <span>{ticket.assignee?.name || ticket.assignee?.userName || 'Unassigned'}</span>
            </div>
            <div className="detail-row">
              <User size={16} />
              <span>Reporter</span>
              <span>{ticket.reporter?.name || ticket.reporter?.userName || '—'}</span>
            </div>
            <div className="detail-row">
              <Building size={16} />
              <span>Customer</span>
              <span>{ticket.customer?.customerName || ticket.customer?.customerCode || '—'}</span>
            </div>
            <div className="detail-row">
              <Calendar size={16} />
              <span>Created</span>
              <span>{ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : '—'}</span>
            </div>
            {ticket.dueDate && (
              <div className="detail-row">
                <Calendar size={16} />
                <span>Due Date</span>
                <span>{new Date(ticket.dueDate).toLocaleDateString()}</span>
              </div>
            )}
          </div>
          {ticket.labels?.length > 0 && (
            <div className="card">
              <h3><Tag size={16} /> Labels</h3>
              <div className="labels-list">
                {ticket.labels.map((l, i) => (
                  <span key={i} className="label-tag">{l}</span>
                ))}
              </div>
            </div>
          )}
          {ticket.attachments?.length > 0 && (
            <div className="card">
              <h3><Paperclip size={16} /> Attachments</h3>
              <div className="attachment-view-list">
                {ticket.attachments.map((a, i) => (
                  <a
                    key={i}
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="attachment-view-item"
                    title="Open/download"
                  >
                    <Paperclip size={14} />
                    <span>{a.fileName}</span>
                    <Download size={14} />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SupportTicketView
